import { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  fetchFactFindAccessRecipients,
  sendFactFindAccessOtp,
  verifyFactFindAccessOtp,
} from '../services/factFindAccess.service';
// This is the component that will be rendered on success
import ClientDetails from '../meeting/FinalFactFind';
import SeoHead from '../components/common/SeoHead';

const OtpInput = ({ value, onChange }) => {
  return (
    <TextField
      value={value}
      onChange={onChange}
      variant="outlined"
      label="Enter OTP"
      fullWidth
      inputProps={{
        maxLength: 6,
        style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
      }}
      placeholder="------"
    />
  );
};

const VerifyAccessPage = () => {
  const [searchParams] = useSearchParams();
  // The ID from the URL is now for the access record, not a file
  const accessId = searchParams.get('accessId');

  const [step, setStep] = useState('loading'); // 'loading', 'request', 'verify', 'success', 'error'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [authorizedRecipients, setAuthorizedRecipients] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  
  const [otp, setOtp] = useState('');
  // This state will hold the clientId after successful verification
  const [verifiedClientId, setVerifiedClientId] = useState('');

  const [timer, setTimer] = useState(60);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  // Step 1: Fetch the list of authorized recipients on page load
  useEffect(() => {
    if (!accessId) {
      setError('No Access ID provided in the link.');
      setStep('error');
      setLoading(false);
      return;
    }

    const fetchRecipients = async () => {
      setLoading(true);
      try {
        const response: any = await fetchFactFindAccessRecipients(accessId as string);
        if (!response.success || !response.recipients) {
          throw new Error(response.message || 'Could not fetch recipient list.');
        }
        setAuthorizedRecipients(response.recipients);
        setStep('request'); // Ready to ask the user to select their email
      } catch (err: any) {
        setError(err.message);
        setStep('error');
      } finally {
        setLoading(false);
      }
    };
    fetchRecipients();
  }, [accessId]);

  useEffect(() => {
    let interval;
    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setIsTimerRunning(false);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timer]);

  const startTimer = () => {
    setTimer(60);
    setIsTimerRunning(true);
  };

  // Step 2: Request the OTP for the selected email
  const handleRequestCode = async () => {
    if (!selectedEmail) {
      setError('Please select your email from the list.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response: any = await sendFactFindAccessOtp(accessId as string, selectedEmail);
      if (!response.success) throw new Error(response.message);
      setStep('verify'); // Move to the OTP entry step
      startTimer();
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify the OTP and get the clientId on success
  const handleVerifyCode = async () => {
    setLoading(true);
    setError('');
    try {
      const response: any = await verifyFactFindAccessOtp(accessId as string, otp, selectedEmail);
      // The backend now returns the clientId needed to render the fact-find
      if (!response.success || !response.clientId) {
        throw new Error(response.message || 'OTP verification failed.');
      }
      
      // Store the verified clientId and move to the success step
      setVerifiedClientId(response.clientId);
      setStep('success');

    } catch (err: any) {
      setError(err.message || 'Invalid OTP or an error occurred.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  // This function renders the correct UI based on the current step
  const renderContent = () => {
    if (step === 'loading') {
      return <CircularProgress />;
    }
    if (step === 'error') {
        return (
            <Alert severity="error">
                {error || 'An unexpected error occurred.'}
                <Button component={RouterLink} to="/" sx={{ml: 2}}>Go Home</Button>
            </Alert>
        );
    }

    // After successful verification, render the ClientDetails component
    if (step === 'success' && verifiedClientId) {
      // We must override localStorage for the ClientDetails component to work for an external user
      localStorage.setItem("clientId", verifiedClientId);
      return <ClientDetails />;
    }

    // The main verification flow UI
    return (
        <Paper
            elevation={3}
            sx={{
            p: 4,
            borderRadius: "12px",
            textAlign: 'center',
            width: '100%',
            maxWidth: '450px',
            }}
        >
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            {step === 'request' && (
                <>
                    <Typography variant="h5" fontWeight="bold">Verification Required</Typography>
                    <Typography color="text.secondary" my={2}>
                        Please select your email address to receive a verification code.
                    </Typography>
                    <FormControl fullWidth sx={{ my: 2 }}>
                        <InputLabel id="recipient-select-label">Select Your Email</InputLabel>
                        <Select
                            labelId="recipient-select-label"
                            value={selectedEmail}
                            label="Select Your Email"
                            onChange={(e) => setSelectedEmail(e.target.value)}
                        >
                            {authorizedRecipients.map((email) => (
                                <MenuItem key={email} value={email}>{email}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" fullWidth onClick={handleRequestCode} disabled={loading || !selectedEmail} sx={{ mt: 2, py: 1.5 }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Request Code'}
                    </Button>
                </>
            )}

            {step === 'verify' && (
                <>
                    <Typography variant="h5" fontWeight="bold">Enter Verification Code</Typography>
                    <Typography color="text.secondary" my={2}>
                        A 6-digit code has been sent to <strong>{selectedEmail}</strong>.
                    </Typography>
                    <Box my={3}>
                        <OtpInput value={otp} onChange={(e) => setOtp(e.target.value)} />
                    </Box>
                    <Button variant="contained" fullWidth onClick={handleVerifyCode} disabled={loading || otp.length !== 6} sx={{ py: 1.5 }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                    </Button>
                    <Button variant="text" fullWidth onClick={handleRequestCode} disabled={isTimerRunning || loading} sx={{ mt: 2 }}>
                        {isTimerRunning ? `Resend Code in ${timer}s` : 'Resend Code'}
                    </Button>
                </>
            )}
        </Paper>
    );
  };

  return (
    <>
      <SeoHead
        title="Verify Access"
        description="Verify your identity to access your ProspoAI account or shared client details."
        path="/verify-access"
        index={false}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: step === 'success' ? 'flex-start' : 'center',
          minHeight: '100vh',
          bgcolor: step === 'success' ? '#fff' : '#f0f2f5',
          p: step === 'success' ? 0 : 2,
        }}
      >
        {renderContent()}
      </Box>
    </>
  );
};

export default VerifyAccessPage;
